/* eslint-disable no-extra-boolean-cast */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { IndustryCertificationType } from 'types/IndustryCertifications.types'

import { TableNotFoundHint } from 'components/TableNotFoundHint'
import { CertificationDropdown } from './CertificationDropdown'
import { ModalAddCertifications } from './ModalAddCertifications/ModalAddCertifications'

import * as S from './styled'
import { format, parseISO } from 'date-fns'
import { Modal } from 'components/Modal'
import { Button } from 'components/Button'

export type CertificationsProps = {
  payload: IndustryCertificationType[]
  addModal?: () => void
  onHandleCertification: (_certificate, _index) => void
  handleDeleteCertification: (_index) => void
}

export function Certifications({
  payload,
  addModal,
  onHandleCertification,
  handleDeleteCertification
}: CertificationsProps) {
  const { t } = useTranslation()

  const [file, setFile] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [openModalCertification, setOpenModalCertification] = useState(false)
  const [selectedCertification, setSelectedCertification] =
    useState<IndustryCertificationType | null>(null)

  function openPDF(file) {
    if (!file) return
    setOpenModalCertification(!openModalCertification)
    setFile(file)
  }

  const addModalClick = () => {
    if (addModal) {
      addModal()
    }
  }

  const handleEditClick = (
    certification: IndustryCertificationType,
    index: number
  ) => {
    setSelectedCertification(certification)
    setSelectedIndex(index)
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedCertification(null)
    setSelectedIndex(null)
  }

  const handleCertification = (
    updatedCertification: IndustryCertificationType
  ) => {
    if (selectedIndex !== null) {
      const updatedPayload = [...payload]
      updatedPayload[selectedIndex] = updatedCertification
      onHandleCertification(updatedCertification, selectedIndex)
    }
  }

  const handleDownload = file => {
    try {
      const link = document.createElement('a')
      link.href = file.src
      link.download = file.filename || 'downloaded-file'
      document.body.appendChild(link)

      window.open(link.href, '_blank')

      document.body.removeChild(link)

      if (file.src.startsWith('blob:')) {
        window.URL.revokeObjectURL(file.src)
      }
    } catch (error) {
      console.error('Erro ao baixar o arquivo:', error)
    }
  }

  return (
    <S.Wrapper data-testid="IndustryCertifications">
      {openModalCertification && (
        <Modal
          content={
            <>
              <S.ContentPdf>
                <iframe
                  className="pdf-viewer"
                  src={`${file.src}#toolbar=0&navpanes=0&scrollbar=0`}
                  allowTransparency={true}
                />
              </S.ContentPdf>
              <Button variant="primary" onClick={() => handleDownload(file)}>
                <p>{t('indCertifications:downloadCertification')}</p>
              </Button>
            </>
          }
          cancelButton={false}
          onExit={() => setOpenModalCertification(!openModalCertification)}
        />
      )}
      <S.TableGroup>
        <S.ScrollWrapper>
          <S.Table id="table-scrolled">
            <tbody>
              <S.Line id="header">
                <S.Header>{t('common:name')}</S.Header>
                <S.Header>{t('indCertifications:acronymous')}</S.Header>
                <S.Header>
                  {t('indCertifications:certificationNumber')}
                </S.Header>
                <S.Header>{t('indCertifications:emissionDate')}</S.Header>
                <S.Header>{t('indCertifications:expirationDate')}</S.Header>
                <S.Header>{t('indCertifications:status')}</S.Header>
              </S.Line>
              {payload &&
                payload.map((each, key) => (
                  <S.Line id="values" key={key}>
                    <S.Column>{each.name}</S.Column>
                    <S.Column>{each.acronym}</S.Column>
                    <S.Column>{each.number}</S.Column>
                    <S.Column>
                      {format(parseISO(each.dtEmitted), 'dd/MM/yyyy')}
                    </S.Column>
                    <S.Column>
                      {format(parseISO(each.dtExpiry), 'dd/MM/yyyy')}
                    </S.Column>
                    {each.status ? (
                      <S.Column>{t('indCertifications:active')}</S.Column>
                    ) : (
                      <S.Column>{t('indCertifications:inactive')}</S.Column>
                    )}
                  </S.Line>
                ))}
            </tbody>
          </S.Table>
        </S.ScrollWrapper>
        <S.Table id="table-fixed">
          <tbody>
            <S.Line id="header">
              <S.Header>{t('common:actions')}</S.Header>
            </S.Line>
            {Boolean(payload && payload.length) &&
              payload.map((each, key) => (
                <S.Line key={key}>
                  <S.Column id="actions-column">
                    <CertificationDropdown
                      setDeleteCertification={() =>
                        handleDeleteCertification(key)
                      }
                      handleEdit={() => handleEditClick(each, key)}
                      openModal={() => openPDF(each.file)}
                    />
                  </S.Column>
                </S.Line>
              ))}
          </tbody>
        </S.Table>
      </S.TableGroup>
      {payload && !payload.length && (
        <TableNotFoundHint onClick={addModalClick} />
      )}
      {isModalOpen && selectedCertification && (
        <ModalAddCertifications
          certification={selectedCertification}
          onExit={handleModalClose}
          index={selectedIndex}
          onHandleCertification={handleCertification}
        />
      )}
    </S.Wrapper>
  )
}
